import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Grid, Tabs, Tab, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import {
  getMarketingVisitFunnelReport, getMarketingUtmRoiReport, getMarketingPagePathsReport,
  getMarketingGeoReport, getMarketingChannelTrendReport, getMarketingDemoFunnelReport,
  getMarketingIdentificationBridgeReport,
} from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import DistributionBar from '../../components/reports/DistributionBar';
import FunnelChart from '../../components/reports/FunnelChart';
import TableWithBars from '../../components/reports/TableWithBars';
import Heatmap from '../../components/reports/Heatmap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import ReportExportButton from '../../components/reports/ReportExportButton';

const fmtPct = (v, d = 1) => v == null ? '—' : `${(v * 100).toFixed(d)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();

function ReportSection({ title, action, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function useReport(loader, filters) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await loader(filters);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [loader, JSON.stringify(filters)]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error };
}

// M1
function VisitFunnelCard({ filters }) {
  const { data, loading, error } = useReport(getMarketingVisitFunnelReport, filters);
  const stages = data?.data?.stages || [];
  return (
    <ReportSection title="M1 · Visit → Lead Funnel">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <FunnelChart
          stages={stages.map((s) => ({ label: s.step, count: s.count }))}
          color="#3b82f6"
        />
      )}
    </ReportSection>
  );
}

// M2
const UTM_GROUPS = [
  { key: 'campaign', label: 'Campaign' },
  { key: 'source', label: 'Source' },
  { key: 'medium', label: 'Medium' },
];
function UtmRoiCard({ filters }) {
  const [groupBy, setGroupBy] = useState('campaign');
  const f = useMemo(() => ({ ...filters, groupBy }), [filters, groupBy]);
  const { data, loading, error } = useReport(getMarketingUtmRoiReport, f);
  const rows = data?.data?.rows || [];
  return (
    <ReportSection
      title="M2 · UTM ROI"
      action={
        <Stack direction="row" spacing={1}>
          <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_, v) => v && setGroupBy(v)}>
            {UTM_GROUPS.map((g) => <ToggleButton key={g.key} value={g.key}>{g.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <ReportExportButton path="/reports/marketing/utm-roi/export.csv" filters={f} filename={`marketing-utm-${groupBy}.csv`} />
        </Stack>
      }
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="sessions"
          columns={[
            { key: 'value', label: groupBy },
            { key: 'sessions', label: 'Sessions', align: 'right' },
            { key: 'leads', label: 'Leads', align: 'right' },
            { key: 'qualified', label: 'Qualified', align: 'right' },
            { key: 'converted', label: 'Converted', align: 'right' },
            { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.convRate) },
          ]}
          rows={rows.map((r, i) => ({ id: i, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// M3
function PagePathsCard({ filters }) {
  const { data, loading, error } = useReport(getMarketingPagePathsReport, filters);
  const entries = data?.data?.topEntries || [];
  const converters = data?.data?.topConverters || [];
  const median = data?.data?.medianPathLength;
  return (
    <ReportSection title="M3 · Page → Lead Path">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Top entry pages</Typography>
            <DistributionBar rows={entries.map((e) => ({ label: e.name, value: e.count }))} valueFormatter={fmtInt} />
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Best converters</Typography>
            <TableWithBars
              barColumn="becameLeads"
              columns={[
                { key: 'page', label: 'Page' },
                { key: 'sessions', label: 'Sessions', align: 'right' },
                { key: 'becameLeads', label: 'Leads', align: 'right' },
                { key: 'rate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.rate) },
              ]}
              rows={converters.map((r, i) => ({ id: i, ...r }))}
            />
            {median != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Median path length to lead: {median} pages
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// M4
function GeoCard({ filters }) {
  const { data, loading, error } = useReport(getMarketingGeoReport, filters);
  const rows = data?.data?.countries || [];
  return (
    <ReportSection
      title="M4 · Geo Conversion"
      action={<ReportExportButton path="/reports/marketing/geo-conversion/export.csv" filters={filters} filename="marketing-geo.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="sessions"
          columns={[
            { key: 'code', label: 'Country', render: (r) => r.code || '—' },
            { key: 'sessions', label: 'Sessions', align: 'right' },
            { key: 'leads', label: 'Leads', align: 'right' },
            { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.convRate) },
          ]}
          rows={rows.map((r, i) => ({ id: r.code || i, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// M5
function ChannelTrendCard({ filters }) {
  const { data, loading, error } = useReport(getMarketingChannelTrendReport, { ...filters, window: filters.window || '90d' });
  const weeks = data?.data?.weeks || [];
  const categories = data?.data?.categories || [];
  return (
    <ReportSection title="M5 · Channel Mix Trend">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : weeks.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No data.</Typography>
      ) : (
        <Heatmap
          rows={categories}
          columns={weeks.map((w) => w.week)}
          rowLabel={(c) => c}
          getValue={(category, week) => {
            const w = weeks.find((x) => x.week === week);
            return w?.categories?.[category] || 0;
          }}
        />
      )}
    </ReportSection>
  );
}

// M6
function DemoFunnelCard({ filters }) {
  const { data, loading, error } = useReport(getMarketingDemoFunnelReport, filters);
  const stages = data?.data?.stages || [];
  return (
    <ReportSection title="M6 · Demo Booking Funnel">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <FunnelChart
          stages={stages.map((s) => ({ label: s.step, count: s.count }))}
          color="#16a34a"
        />
      )}
    </ReportSection>
  );
}

// M7
function IdentificationBridgeCard({ filters }) {
  const { data, loading, error } = useReport(getMarketingIdentificationBridgeReport, filters);
  const d = data?.data || {};
  const total = (d.anonymousSessions || 0) + (d.identifiedLater || 0) + (d.identifiedSameSession || 0);
  return (
    <ReportSection title="M7 · Anonymous → Identified Bridge">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><KpiCard label="Total sessions" value={fmtInt(total)} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Anonymous" value={fmtInt(d.anonymousSessions)} tone="neutral" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Identified same session" value={fmtInt(d.identifiedSameSession)} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Identified later" value={fmtInt(d.identifiedLater)} tone="info" /></Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Time to identify</Typography>
          <DistributionBar
            rows={(d.buckets || []).map((b) => ({ label: b.label, value: b.count, color: '#3b82f6' }))}
            valueFormatter={fmtInt}
          />
        </>
      )}
    </ReportSection>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'visit-funnel', label: 'Visit Funnel (M1)' },
  { key: 'utm', label: 'UTM ROI (M2)' },
  { key: 'pages', label: 'Pages (M3)' },
  { key: 'geo', label: 'Geo (M4)' },
  { key: 'channel-trend', label: 'Channel Trend (M5)' },
  { key: 'demo', label: 'Demo Funnel (M6)' },
  { key: 'bridge', label: 'Identification (M7)' },
];

export default function MarketingReportPage() {
  const [filters, setFilters] = useState({ window: '30d' });
  const [tab, setTab] = useState('overview');
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <InsightsOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Marketing Site Reports</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} />
      </Stack>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>
      {tab === 'overview' && (
        <>
          <VisitFunnelCard filters={filters} />
          <UtmRoiCard filters={filters} />
          <DemoFunnelCard filters={filters} />
        </>
      )}
      {tab === 'visit-funnel' && <VisitFunnelCard filters={filters} />}
      {tab === 'utm' && <UtmRoiCard filters={filters} />}
      {tab === 'pages' && <PagePathsCard filters={filters} />}
      {tab === 'geo' && <GeoCard filters={filters} />}
      {tab === 'channel-trend' && <ChannelTrendCard filters={filters} />}
      {tab === 'demo' && <DemoFunnelCard filters={filters} />}
      {tab === 'bridge' && <IdentificationBridgeCard filters={filters} />}
    </Box>
  );
}
